import { useEffect, useRef } from "react";

export default function Hero() {
  const lineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = lineRef.current;
    if (!el) return;
    const timer = setTimeout(() => {
      el.style.width = "80px";
    }, 600);
    return () => clearTimeout(timer);
  }, []);

  const scrollToCTA = () => {
    const el = document.getElementById("booking");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToContact = () => {
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-[#080808]">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/77bec96147d07ec422d98c3c3d4f4d35.jpg"
          alt="Eastern European city skyline at night"
          title="Eastern Europe Market Entry"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#080808]/70 via-[#080808]/50 to-[#080808]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#080808]/60 via-transparent to-[#080808]/60" />
      </div>

      {/* Gold top line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C9A84C]/60 to-transparent" />

      {/* Content */}
      <div className="relative z-10 w-full flex flex-col items-center text-center px-6 pt-32 pb-24">
        {/* Eyebrow */}
        <div className="flex items-center gap-4 mb-10 opacity-0 animate-[fadeInDown_0.8s_ease_0.2s_forwards]">
          <div className="w-12 h-px bg-[#C9A84C]/60" />
          <span className="text-[#C9A84C] text-xs tracking-[0.3em] uppercase font-light">
            AI-Powered Market Expansion
          </span>
          <div className="w-12 h-px bg-[#C9A84C]/60" />
        </div>

        {/* Headline */}
        <h1
          className="font-['Cormorant_Garamond'] text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-light text-white leading-[1.05] tracking-tight max-w-5xl opacity-0 animate-[fadeInUp_0.9s_ease_0.4s_forwards]"
        >
          Enter Eastern Europe.<br />
          <em className="text-[#C9A84C] not-italic">Close deals.</em><br />
          Scale faster.
        </h1>

        {/* Gold line */}
        <div
          ref={lineRef}
          className="mt-8 h-px bg-[#C9A84C] transition-all duration-1000 ease-out"
          style={{ width: "0px" }}
        />

        {/* Subheadline */}
        <p className="mt-8 text-white/60 text-base sm:text-lg font-light leading-relaxed max-w-2xl font-['Outfit'] tracking-wide opacity-0 animate-[fadeInUp_0.9s_ease_0.8s_forwards]">
          We help Western companies build sales pipelines and win clients in Eastern Europe
          using <strong className="text-white/90 font-normal">AI-powered systems</strong> and local expertise.
        </p>

        {/* CTA */}
        <div className="mt-12 flex flex-col sm:flex-row gap-4 items-center opacity-0 animate-[fadeInUp_0.9s_ease_1s_forwards]">
          <a
            href="https://calendly.com/alex-emcalu/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="group px-10 py-4 bg-[#C9A84C] text-[#080808] text-sm font-semibold tracking-[0.2em] uppercase hover:bg-white transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            Book a Strategy Call
            <span className="inline-block ml-2 transition-transform duration-300 group-hover:translate-x-1">→</span>
          </a>
          <button
            onClick={scrollToContact}
            className="group px-10 py-4 border border-white/20 text-white/70 text-sm font-light tracking-[0.2em] uppercase hover:border-[#C9A84C] hover:text-[#C9A84C] transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            Contact Us
          </button>
        </div>

        {/* Trust line */}
        <p className="mt-10 text-white/35 text-xs tracking-[0.25em] uppercase font-light font-['Outfit'] opacity-0 animate-[fadeInUp_0.9s_ease_1.2s_forwards]">
          Trusted by companies expanding into Romania, Moldova and CEE markets
        </p>

        {/* Country flags / logos area */}
        <div className="mt-6 flex items-center gap-6 opacity-0 animate-[fadeInUp_0.9s_ease_1.4s_forwards]">
          {["🇫🇷 France", "🇩🇪 Germany", "🇬🇧 UK", "🇧🇪 Benelux"].map((flag) => (
            <span key={flag} className="text-white/30 text-xs font-light tracking-wider font-['Outfit']">
              {flag}
            </span>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-0 animate-[fadeIn_1s_ease_2s_forwards]">
        <span className="text-white/30 text-[10px] tracking-[0.3em] uppercase font-['Outfit']">Scroll</span>
        <div className="w-px h-12 bg-gradient-to-b from-[#C9A84C]/60 to-transparent animate-pulse" />
      </div>
    </section>
  );
}
